import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clientActions } from 'core/client/clientReducers'
import useReactRouter from 'use-react-router'
import useListAction from 'core/hooks/useListAction'
import { listLimitRanges, updateLimitRange } from '../actions'
import { ILimitRangeDetailsPageTabs, ILimitRangeSelector } from '../model'
import { limitRangesSelector } from '../selectors'
import useUpdateAction from 'core/hooks/useUpdateAction'
import DocumentMeta from 'core/components/DocumentMeta'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import { routes } from 'core/utils/routes'
import LimitRangesOverview from './LimitRangesOverview'
import EntityYamlPage from 'k8s/components/common/entity/entity-yaml-page'
import ApiClient from 'api-client/ApiClient'
import { isEmpty } from 'ramda'
import jsYaml from 'js-yaml'
import { pathStrOr } from 'utils/fp'
import OverviewActions from 'core/elements/overview/OverviewActions'
import DeleteLimitRangeDialog from '../DeleteLimitRangeDialog'
import usePluginRouter from 'core/hooks/usePluginRouter'

const { qbert } = ApiClient.getInstance()

const actions = {
  rightActions: [
    {
      label: 'Delete',
      icon: 'trash-alt',
      DialogComponent: DeleteLimitRangeDialog,
    },
  ],
}

export default function LimitRangesDetailsPage() {
  const dispatch = useDispatch()
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  const { match } = useReactRouter()
  const { id, clusterId } = match.params
  const { loading, reload } = useListAction(listLimitRanges, { params: { clusterId } })
  const limitRanges: ILimitRangeSelector[] = useSelector(limitRangesSelector)
  const limitRange = useMemo(() => limitRanges.find((limitRange) => limitRange.id === id), [
    id,
    limitRanges,
  ])

  const { update, updating } = useUpdateAction(updateLimitRange)

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        clusterId: limitRange?.clusterName || clusterId,
        id: limitRange?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [limitRange?.clusterName, limitRange?.name, id, clusterId])

  const getLimitRangeYaml = useCallback(async () => {
    if (isEmpty(limitRange)) return undefined
    return qbert.getLimitRange(limitRange.clusterId, limitRange.namespace, limitRange.name)
  }, [limitRange])

  const handleLimitRangeUpdate = async (yaml) => {
    const body = jsYaml.load(yaml)
    const namespace = pathStrOr('', 'metadata.namespace', body)
    const name = pathStrOr('', 'metadata.name', body)
    await update({
      id,
      clusterId,
      namespace,
      name,
      body,
    })
  }

  return (
    <>
      <DocumentMeta title="Limit Ranges Overview" breadcrumbs />
      <div>
        <OverviewActions actions={actions} entity={limitRange} />
      </div>
      <Tabs route={routes[routePath].limitRanges.details}>
        <Tab label="Overview" value={ILimitRangeDetailsPageTabs.Overview}>
          <LimitRangesOverview limitRange={limitRange} loading={loading} reload={reload} />
        </Tab>
        <Tab label="YAML" value={ILimitRangeDetailsPageTabs.Yaml}>
          <EntityYamlPage
            entityType="Limit Ranges"
            entityName={limitRange?.name}
            getYamlFn={getLimitRangeYaml}
            handleUpdate={handleLimitRangeUpdate}
            loading={loading || updating}
          />
        </Tab>
      </Tabs>
    </>
  )
}
